/**
 * Lazy loader for face-api.js (the maintained @vladmandic fork) plus its models,
 * both pulled from the jsDelivr CDN at runtime so nothing is bundled into the main
 * app. Used by the face-enrolment step (employee drawer) and the attendance kiosk.
 *
 * The 128-float descriptor produced here is what the server stores/compares — the
 * raw camera image never leaves the browser.
 */
declare global {
  interface Window { faceapi?: any; __faceModelsLoaded?: boolean }
}

const SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/dist/face-api.js';
const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model';

let loader: Promise<any> | null = null;

/** Load the face-api script + models once; resolves with the global `faceapi`. */
export function loadFaceApi(): Promise<any> {
  if (window.faceapi && window.__faceModelsLoaded) return Promise.resolve(window.faceapi);
  if (loader) return loader;
  loader = new Promise<any>((resolve, reject) => {
    const loadModels = async () => {
      const f = window.faceapi;
      if (!f) return reject(new Error('Face engine failed to initialise.'));
      try {
        await f.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        await f.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
        await f.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
        window.__faceModelsLoaded = true;
        resolve(f);
      } catch {
        loader = null;
        reject(new Error('Could not download the face-recognition models. Check the internet connection and retry.'));
      }
    };
    if (window.faceapi) { loadModels(); return; }
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = loadModels;
    script.onerror = () => { loader = null; reject(new Error('Could not load the face-recognition engine. Check the internet connection.')); };
    document.head.appendChild(script);
  });
  return loader;
}

/** Compute a single 128-float face descriptor from a video/image element, or null if no face. */
export async function computeDescriptor(el: HTMLVideoElement | HTMLImageElement): Promise<number[] | null> {
  const f = await loadFaceApi();
  const options = new f.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.5 });
  const result = await f.detectSingleFace(el, options).withFaceLandmarks().withFaceDescriptor();
  if (!result?.descriptor) return null;
  return Array.from(result.descriptor as Float32Array).map((n) => Number(n));
}
